/**
 * Health counters: named values a node reports about itself as a tree of groups, read from
 * its `GET /api/v1/health/counters`; what each is comes from
 * `GET /api/v1/health/counters/descriptions`.
 */

import { z } from 'zod';

/**
 * How a counter's value reads. A frozen object plus its union rather than an enum, because
 * the unit arrives as JSON.
 */
export const CounterUnit = {
    Count: 'count',
    Bytes: 'bytes',
    /** A rate, as a bitrate is stated. */
    BitsPerSecond: 'bitsPerSecond',
    Percent: 'percent',
    /** A duration in milliseconds. */
    DurationMs: 'durationMs',
    /** A moment, in milliseconds since the epoch. */
    TimeMs: 'timeMs',
    /** One of a fixed set of words, such as a camera status. */
    State: 'state',
    Text: 'text',
} as const;

export type CounterUnit = (typeof CounterUnit)[keyof typeof CounterUnit];

/** What a client needs to show a health counter. */
export const HealthCounterDescriptionSchema = z.object({
    /**
     * Where the counter is in the tree: names joined by `/`, with `{id}` for an id level and a
     * named placeholder, as `{stream}`, for a part of an item.
     */
    path: z.string(),
    /** The name a person reads, beside the value. */
    name: z.string(),
    /** What the counter measures, in a sentence or two. */
    description: z.string().optional(),
    unit: z.string(),
    /** The counter exists for the rules; the panel shows the rest of its group without it. */
    hidden: z.boolean().optional(),
    /** For a bytes counter: the sibling it is a part of, shown as the share beside the value. */
    shareOf: z.string().optional(),
    /** The sibling that holds what this counter is meant to be; the two share one column. */
    target: z.string().optional(),
});
export type HealthCounterDescription = z.infer<typeof HealthCounterDescriptionSchema>;

/** How a rule compares a counter with its limit, as the node serves it. */
export const RuleOperator = {
    Equal: '==',
    NotEqual: '!=',
    Greater: '>',
    GreaterOrEqual: '>=',
    Less: '<',
    LessOrEqual: '<=',
    /** The counter's word is one of the limit's. */
    In: 'in',
    NotIn: 'notIn',
} as const;

export type RuleOperator = (typeof RuleOperator)[keyof typeof RuleOperator];

/** A rule's limit: a number, a word for a state counter, or a list of words with `in`. */
export const HealthRuleLimitSchema = z.union([z.number(), z.string(), z.array(z.string())]);
export type HealthRuleLimit = z.infer<typeof HealthRuleLimitSchema>;

/**
 * A problem a counter shows, from the node's `GET /api/v1/health/rules`: the counter past one
 * of the limits for long enough is the problem at that stage.
 */
export const HealthRuleSchema = z.object({
    /** Names the problem; the type of the health message raised. */
    type: z.string(),
    /** Path of the counter watched, as a description's `path`. */
    counter: z.string(),
    op: z.string(),
    /** One limit per stage of the problem; the error is the stricter one. */
    limits: z.object({
        warning: HealthRuleLimitSchema.optional(),
        error: HealthRuleLimitSchema.optional(),
    }),
    /** How long a stage holds before it is reported, and how long none does before the end. */
    holdSeconds: z.number(),
    text: z.string(),
});
export type HealthRule = z.infer<typeof HealthRuleSchema>;

/** The counter tree as its node serves it: groups and counters by name, a group a tree too. */
export const HealthCounterValuesSchema = z.record(z.string(), z.unknown());
export type HealthCounterValues = z.infer<typeof HealthCounterValuesSchema>;

/** One node's counters as the panel holds them: the values, reloaded while shown, and the
 * descriptions, read once. Each is null until its first answer. */
export interface HealthCountersSnapshot {
    values: HealthCounterValues | null;
    descriptions: HealthCounterDescription[] | null;
    rules: HealthRule[] | null;
    /** When the values were last read, for the "updated ago" note; 0 before the first. */
    valuesReadAtMs: number;
    isLoading: boolean;
    /** Why the values or the descriptions could not be read; the last read ones stay on screen. */
    error: Error | null;
    /** Why the rules could not be read; the counters are drawn without their limits. */
    rulesError: Error | null;
}

/** A rule's limit as drawn beside the counter it watches. */
export interface CounterLimit {
    op: string;
    limit: HealthRuleLimit;
    holdSeconds: number;
    severity: string;
    /** The rule's condition holds at the current value, whether or not for long enough yet. */
    isLimitMet: boolean;
}

/** One counter's value, ready to render in a row or a table cell. */
export interface CounterCell {
    value: unknown;
    unit: string;
    text: string;
    /** The value as a share of the counter it is a part of, when described so; drawn as a bar. */
    sharePercent?: number;
    limits: CounterLimit[];
    /** The severity of the worst breached limit; null with none. */
    metLimitSeverity: string | null;
}

/** One of a group's own counters. */
export interface CounterRow extends CounterCell {
    key: string;
    name: string;
    /** What the counter measures, for the row's tooltip. */
    description?: string;
}

/**
 * A column of a group's item table: one counter every item of the id level may have, or, with
 * a target, that counter and its target side by side under the one name.
 */
export interface CounterColumn {
    key: string;
    name: string;
    description?: string;
    unit: string;
    shareOf?: string;
    /** The key of the target counter's cell, shown beside this column's own. */
    target?: string;
}

/** One camera, storage, GPU or analytics agent of a group: a row of its item table. */
export interface CounterItem {
    key: string;
    /** The id of the item the row belongs to; the rows of one item follow each other. */
    item: string;
    title: string;
    /** The item's `status` counter, shown as a dot before its title. */
    status?: string;
    /** The part of the item this row is, a camera's stream, when the item is split into parts. */
    part?: string;
    /** By column key; a column the item does not report is absent. */
    cells: Record<string, CounterCell>;
}

/** The current issues of a server that rules on a group's counters raised. */
export interface GroupIssueCount {
    errors: number;
    warnings: number;
    /** Their types, errors' first; the first is the tile the issues view opens on. */
    types: string[];
}

/** One group of a node's counters: its own counters, then its items as a table. */
export interface CounterGroup {
    key: string;
    title: string;
    /** The first own counters in a line, for the group's header while it is closed. */
    summary: string;
    rows: CounterRow[];
    columns: CounterColumn[];
    /** The rows of the item table; an item split into parts is a row per part, titled alike. */
    items: CounterItem[];
    /** What the parts of an item are, as the descriptions call the level: `stream`. */
    partLevel?: string;
    issues: GroupIssueCount;
}
